import type React from 'react';
import { useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';

import { StateCodes } from '@makemydeal/dr-shared-ui-utils';
import { kebabCase } from '@makemydeal/dr-common-utils';

import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import type { TextInputValue } from '@interstate/components/TextInput';
import type { InterstateOnChangeCallback, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import {
    LIEN_HOLDER_ACCOUNT_NUMBER,
    LIEN_HOLDER_PHONE_NUMBER,
    LIEN_HOLDER_ADDRESS_BANK,
    LIEN_HOLDER_ADDRESS_CITY,
    LIEN_HOLDER_ADDRESS_STATE,
    LIEN_HOLDER_ADDRESS_ZIP,
    PAYOFF_DUE_DATE,
    PER_DIEM
} from '../../../constants';
import type { LienholderFormData } from '../../../types';
import { extractValueFromDatePickerEvent, getStateCodesSelectInputValues, useHandlePerDiemChange } from './utils';
import { LIENHOLDER_FORM_FIELDS } from './constants';
import type { CommitAddressFieldIfChanged, CommitFieldIfChanged } from '../../../types';
import { useSingleTradeInFormProvider } from '../MultiTradeIn/TradeInFormProvider';
import type { LienholderFormInputsProps } from './lienholderFormTypes';

const validationIconStyles = {
    '.interstate-exclamation-circle-icon': { flexShrink: 0 }
};

export const useLienholderFormInputs = ({
    isTradeInCompleted,
    lienholderData,
    setLienholderData,
    isOutsideLienholder = false,
    isEditDisabled,
    softSaveLienHolderData,
    clearFields
}: LienholderFormInputsProps) => {
    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, StateCodes);
    const isFieldDisabled = isEditDisabled || !isTradeInCompleted;

    const { lienholderAccountNumber, lienholderPhoneNumber, lienholderAddress, payoffDueDate, perDiem } = lienholderData;

    const { tradeInValidator: lienholderValidator, tradeInFormErrors: lienholderFormErrors } = useSingleTradeInFormProvider();
    const formErrors = lienholderFormErrors.lienholder;

    const previousLienHolderValues = useRef<LienholderFormData>(lienholderData);

    // Map form field names to engine validation paths
    const fieldsToEnginePath: Record<string, string> = useMemo(
        () => ({
            [LIENHOLDER_FORM_FIELDS.LIENHOLDER_ACCOUNT_NUMBER]: 'lienholder.accountNumber',
            [LIENHOLDER_FORM_FIELDS.ADDRESS]: 'lienholder.streetAddress',
            [LIENHOLDER_FORM_FIELDS.CITY]: 'lienholder.city',
            [LIENHOLDER_FORM_FIELDS.STATE_PROVINCE]: 'lienholder.state',
            [LIENHOLDER_FORM_FIELDS.POSTAL_CODE]: 'lienholder.zip',
            [LIENHOLDER_FORM_FIELDS.LIENHOLDER_PHONE_NUMBER]: 'lienholder.phoneNumber',
            [LIENHOLDER_FORM_FIELDS.PAYOFF_DUE_DATE]: 'lienholder.payoffDueDate'
        }),
        []
    );

    const onChange = useCallback(
        (field: keyof typeof lienholderData): InterstateOnChangeCallback<any> =>
            (e: InterstateOnChangeEvent<any>) => {
                setLienholderData((prevLienholderData) => ({
                    ...prevLienholderData,
                    [field]: e.target.value
                }));

                const fieldPath = fieldsToEnginePath[LIENHOLDER_FORM_FIELDS.LIENHOLDER_PHONE_NUMBER];
                if (fieldPath) {
                    const isValid = e.isValid as boolean;
                    lienholderValidator.setError(fieldPath, isValid, '');
                    lienholderValidator.commit([fieldPath], { validate: false });
                }
            },
        [setLienholderData, lienholderValidator, fieldsToEnginePath]
    );

    const handleSoftSaveOnEnterKey =
        (commitChanges: ReturnType<CommitAddressFieldIfChanged | CommitFieldIfChanged>) =>
        (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
            if (event.key === 'Enter') {
                commitChanges();
            }
        };

    const commitAddressFieldIfChanged: CommitAddressFieldIfChanged =
        (fieldName: keyof LienholderFormData['lienholderAddress']) => () => {
            if (previousLienHolderValues.current.lienholderAddress[fieldName] !== lienholderData.lienholderAddress[fieldName]) {
                previousLienHolderValues.current = {
                    ...previousLienHolderValues.current,
                    lienholderAddress: {
                        ...previousLienHolderValues.current.lienholderAddress,
                        [fieldName]: lienholderData.lienholderAddress[fieldName]
                    }
                };
                softSaveLienHolderData?.();
            }
        };

    const commitFieldIfChanged: CommitFieldIfChanged = (fieldName: keyof LienholderFormData) => () => {
        if (previousLienHolderValues.current[fieldName] !== lienholderData[fieldName]) {
            previousLienHolderValues.current = {
                ...previousLienHolderValues.current,
                [fieldName]: lienholderData[fieldName]
            } as LienholderFormData;
            softSaveLienHolderData?.();
        }
    };

    useEffect(() => {
        if (previousLienHolderValues.current.payoffDueDate?.toString() !== payoffDueDate?.toString()) {
            commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.PAYOFF_DUE_DATE)();
        }
    }, [payoffDueDate]);

    const validateLienholderDetailsBundle = () => {
        lienholderValidator.validatePath('lienholder.accountNumber');
        lienholderValidator.validatePath('lienholder.streetAddress');
        lienholderValidator.validatePath('lienholder.city');
        lienholderValidator.validatePath('lienholder.state');
        lienholderValidator.validatePath('lienholder.zip');
    };

    useLayoutEffect(() => {
        setLienholderProperties();
        validateLienholderDetailsBundle();
    }, [lienholderValidator]);

    const setLienholderProperties = (useEmpty = false) => {
        lienholderValidator.setValues(
            {
                lienholder: {
                    accountNumber: useEmpty ? '' : lienholderAccountNumber || '',
                    streetAddress: useEmpty ? '' : lienholderAddress.address || '',
                    city: useEmpty ? '' : lienholderAddress.city || '',
                    state: useEmpty ? '' : lienholderAddress.stateProvince || '',
                    zip: useEmpty ? '' : lienholderAddress.postalCode || ''
                }
            },
            { mode: 'merge' }
        );
    };

    const clearLienholderErrors = (useEmpty = false) => {
        setLienholderProperties(useEmpty);
        lienholderValidator.clearErrors();
        const allPaths = ['lienholder.name', ...Object.values(fieldsToEnginePath)];
        lienholderValidator.commit(allPaths, { validate: true });
    };

    // Clear errors on unmount (e.g., when switching from CASH to FINANCE/LEASE)
    useEffect(() => {
        return () => {
            if (isOutsideLienholder) {
                clearLienholderErrors(true);
            }
        };
    }, []);

    // Clear errors when "Clear All" button is clicked (parent already cleared data in Redux)
    useEffect(() => {
        if (isOutsideLienholder) {
            clearLienholderErrors();
        }
    }, [clearFields, isOutsideLienholder, lienholderValidator]);

    const onLienholderAccountNumberChange = (e: InterstateOnChangeEvent<TextInputValue>) => {
        const value = e.target.value;
        setLienholderData((prevLienholderData) => ({
            ...prevLienholderData,
            lienholderAccountNumber: String(value ?? '')
        }));

        const fieldPath = fieldsToEnginePath[LIENHOLDER_FORM_FIELDS.LIENHOLDER_ACCOUNT_NUMBER];
        lienholderValidator.setValue(fieldPath, String(value ?? ''), { validate: true, clearErrors: false });
        validateLienholderDetailsBundle();
    };

    const onLienholderAddressChange = (field: keyof typeof lienholderAddress) => (e: InterstateOnChangeEvent<TextInputValue>) => {
        const fieldValue = e.target.value;
        const trimmedValue =
            (field === LIENHOLDER_FORM_FIELDS.ADDRESS || field === LIENHOLDER_FORM_FIELDS.CITY) && typeof fieldValue === 'string'
                ? fieldValue.trim()
                : fieldValue;

        setLienholderData((prevLienholderData) => ({
            ...prevLienholderData,
            lienholderAddress: {
                ...prevLienholderData.lienholderAddress,
                [field]: trimmedValue
            }
        }));

        // Validate using the new engine - always use lienholder.* paths
        const fieldPath = fieldsToEnginePath[field];
        if (fieldPath) {
            lienholderValidator.setValue(fieldPath, String(trimmedValue), { validate: true, clearErrors: false });
        }
        validateLienholderDetailsBundle();
    };
    const {
        value: perDiemValue,
        handleChange: perDiemChange,
        handleBlur: perDiemBlur
    } = useHandlePerDiemChange(perDiem, onChange, commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.PER_DIEM));

    const onPayoffDueDateChange = (e: InterstateOnChangeEvent<DatePickerEventValue>) => {
        const value = extractValueFromDatePickerEvent(e);
        setLienholderData((prev) => ({ ...prev, payoffDueDate: value }));

        const fieldPath = fieldsToEnginePath[LIENHOLDER_FORM_FIELDS.PAYOFF_DUE_DATE];
        if (fieldPath) {
            const isValid = e.isValid as boolean;
            lienholderValidator.setError(fieldPath, isValid, '');
            lienholderValidator.commit([fieldPath], { validate: false });
        }
    };

    const minPayoffDueDate = new Date();
    minPayoffDueDate.setDate(minPayoffDueDate.getDate() + 1);

    const selectOnFocusSelector = featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap;

    return {
        lienholderFieldProps: {
            lienholderData,
            setLienholderData,
            isEditDisabled,
            softSaveLienHolderData,
            isOutsideLienholder,
            isTradeInCompleted
        },
        accountNumberProps: {
            'data-testid': 'lienholder-account-number',
            id: 'lienholder-account-number',
            className: 'LienholderForm-accountNumber',
            label: LIEN_HOLDER_ACCOUNT_NUMBER,
            name: kebabCase(LIEN_HOLDER_ACCOUNT_NUMBER),
            onChange: onLienholderAccountNumberChange,
            onBlur: commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.LIENHOLDER_ACCOUNT_NUMBER),
            onKeyDown: handleSoftSaveOnEnterKey(commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.LIENHOLDER_ACCOUNT_NUMBER)),
            value: lienholderAccountNumber,
            hasError: formErrors.accountNumber.hasError,
            errorMessage: formErrors.accountNumber.errorMessage,
            disabled: isFieldDisabled,
            size: 'sm' as const,
            selectOnFocusSelector
        },
        payoffDueDateProps: {
            id: 'payoff-due-date-input',
            'data-testid': 'payoff-due-date-input',
            value: payoffDueDate as Date,
            onChange: onPayoffDueDateChange,
            minDate: minPayoffDueDate,
            errorMessage: '',
            hasError: false,
            sx: { minWidth: '5rem' },
            disabled: isFieldDisabled,
            label: PAYOFF_DUE_DATE,
            size: 'sm' as const
        },
        perDiemProps: {
            inputPrefix: '$',
            autoInsertCommas: true,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 2,
            'data-testid': 'per-diem',
            label: PER_DIEM,
            name: kebabCase(PER_DIEM),
            onChange: perDiemChange,
            onKeyDown: handleSoftSaveOnEnterKey(commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.PER_DIEM)),
            onBlur: perDiemBlur,
            placeholder: '0.00',
            value: perDiemValue,
            disabled: isFieldDisabled,
            size: 'sm' as const,
            selectOnFocusSelector
        },
        addressProps: {
            'data-testid': isOutsideLienholder ? 'outside-lienholder-address-bank' : 'lienholder-address-bank',
            label: LIEN_HOLDER_ADDRESS_BANK,
            name: kebabCase(LIEN_HOLDER_ADDRESS_BANK),
            onChange: onLienholderAddressChange(LIENHOLDER_FORM_FIELDS.ADDRESS),
            onBlur: commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.ADDRESS),
            onKeyDown: handleSoftSaveOnEnterKey(commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.ADDRESS)),
            value: lienholderAddress.address,
            hasError: formErrors.streetAddress.hasError,
            errorMessage: formErrors.streetAddress.errorMessage,
            sx: validationIconStyles,
            disabled: isFieldDisabled,
            size: 'sm' as const,
            selectOnFocusSelector
        },
        cityProps: {
            'data-testid': isOutsideLienholder ? 'outside-lienholder-address-city' : 'lienholder-address-city',
            label: LIEN_HOLDER_ADDRESS_CITY,
            name: kebabCase(LIEN_HOLDER_ADDRESS_CITY),
            onChange: onLienholderAddressChange(LIENHOLDER_FORM_FIELDS.CITY),
            onBlur: commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.CITY),
            onKeyDown: handleSoftSaveOnEnterKey(commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.CITY)),
            value: lienholderAddress.city,
            hasError: formErrors.city.hasError,
            errorMessage: formErrors.city.errorMessage,
            sx: validationIconStyles,
            disabled: isFieldDisabled,
            size: 'sm' as const,
            selectOnFocusSelector
        },
        stateProps: {
            'data-testid': isOutsideLienholder ? 'outside-lienholder-address-state' : 'lienholder-address-state',
            label: LIEN_HOLDER_ADDRESS_STATE,
            name: kebabCase(LIEN_HOLDER_ADDRESS_STATE),
            options: stateSelectInputValues,
            onChange: onLienholderAddressChange(LIENHOLDER_FORM_FIELDS.STATE_PROVINCE),
            onBlur: commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.STATE_PROVINCE),
            value: lienholderAddress.stateProvince,
            errorMessage: formErrors.state.errorMessage,
            hasError: formErrors.state.hasError,
            disabled: isFieldDisabled,
            size: 'sm' as const,
            allowRemoval: true,
            selectOnFocusSelector
        },
        zipProps: {
            'data-testid': isOutsideLienholder ? 'outside-lienholder-address-zip' : 'lienholder-address-zip',
            label: LIEN_HOLDER_ADDRESS_ZIP,
            country: 'US' as const,
            name: kebabCase(LIEN_HOLDER_ADDRESS_ZIP),
            onChange: onLienholderAddressChange(LIENHOLDER_FORM_FIELDS.POSTAL_CODE),
            onBlur: commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.POSTAL_CODE),
            onKeyDown: handleSoftSaveOnEnterKey(commitAddressFieldIfChanged(LIENHOLDER_FORM_FIELDS.POSTAL_CODE)),
            value: lienholderAddress.postalCode,
            hasError: formErrors.zip.hasError,
            errorMessage: formErrors.zip.errorMessage,
            sx: validationIconStyles,
            disabled: isFieldDisabled,
            size: 'sm' as const,
            selectOnFocusSelector
        },
        phoneProps: {
            label: LIEN_HOLDER_PHONE_NUMBER,
            name: kebabCase(LIEN_HOLDER_PHONE_NUMBER),
            onChange: onChange(LIENHOLDER_FORM_FIELDS.LIENHOLDER_PHONE_NUMBER),
            value: lienholderPhoneNumber,
            onBlur: commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.LIENHOLDER_PHONE_NUMBER),
            onKeyDown: handleSoftSaveOnEnterKey(commitFieldIfChanged(LIENHOLDER_FORM_FIELDS.LIENHOLDER_PHONE_NUMBER)),
            'data-testid': isOutsideLienholder ? 'outside-lienholder-phone-number' : 'lienholder-phone-number',
            disabled: isFieldDisabled,
            size: 'sm' as const,
            selectOnFocusSelector
        }
    };
};
